
import React, { useEffect, useRef, useState } from "react";
import { FAIR_SIMULATION_OPTIONS } from "../config/fairSettings";
import axios from "axios";
import { ensureCsrfToken } from "../lib/csrf";
import { useHelpContext } from "../help/HelpContext";
import { normalizePlatformSettings } from "../lib/platformSettings";
import { useAccess } from "../access/AccessContext";

const sections = [
  { id: "governance", label: "Governance" },
  { id: "capital-efficiency", label: "Capital Efficiency" },
  { id: "quantification", label: "Quantification" },
  { id: "dashboards", label: "Dashboards" },
  { id: "branding", label: "Branding" },
  { id: "platform", label: "Platform" },
];


const RISK_APPETITE_PRESETS = {
  Low: {
    riskAppetiteP95: 3000000,
    riskAppetiteEAL: 1000000,
  },
  Moderate: {
    riskAppetiteP95: 5000000,
    riskAppetiteEAL: 1500000,
  },
  High: {
    riskAppetiteP95: 8000000,
    riskAppetiteEAL: 2500000,
  },
  Aggressive: {
    riskAppetiteP95: 12000000,
    riskAppetiteEAL: 4000000,
  },
};

const getMatchingRiskAppetite = (settings) => {
  for (const [name, preset] of Object.entries(RISK_APPETITE_PRESETS)) {
    if (
      preset.riskAppetiteP95 === settings.riskAppetiteP95 &&
      preset.riskAppetiteEAL === settings.riskAppetiteEAL
    ) {
      return name;
    }
  }

  return "Custom";
};

const DEFAULT_SETTINGS = {
  riskAppetite: "Moderate",
  criticalExposureThreshold: 5000000,
  boardEscalationThreshold: "Critical",
  delayedInitiativesThreshold: 5,

  // monteCarloIterations: 50000,
  simulations: 50000,
  confidenceLevel: "P95",
  currency: "USD",
  lossModel: "Triangular",
  economicHorizonYears: 1,

  defaultDashboard: "Executive",
  presentationMode: false,
  refreshFrequency: "Daily",

  organizationName: "PHD Consulting",
  reportTheme: "Executive Dark",

  aiGuidance: true,
  executiveInterpretation: true,
  governanceInsights: true,

  seedMode: "Fixed",
  seedValue: 42,

  riskAppetiteP95: 5000000,
  riskAppetiteEAL: 1500000,
  capitalEfficiencyPolicyId: null,
  capitalEfficiencyPolicyVersion: null,
  marginalCapitalEfficiencyHurdle: null,
  marginalRetentionCoverageEfficiencyHurdle: null,
  capitalEfficiencyMandatoryAppetiteDimensions: [],
};

const Field = ({ label, children, help }) => (
  <div>
    <label className="text-sm font-medium text-slate-700">{label}</label>
    <div className="mt-2">{children}</div>
    {help && <p className="text-xs text-slate-400 mt-1">{help}</p>}
  </div>
);

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white/85 backdrop-blur-sm border border-white/70 rounded-2xl p-6 shadow-sm">
    <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
    {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">{children}</div>
  </div>
);

export default function PlatformSettings() {
  const access = useAccess();
  const canConfigure = access.canConfigure("digital_risk.change_platformsettings");
  const [section, setSection] = useState("governance");
  const { setCurrentViewId } = useHelpContext();

  useEffect(() => {
    setCurrentViewId(section);
    return () => setCurrentViewId(null);
  }, [section, setCurrentViewId]);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const settingsRef = useRef(DEFAULT_SETTINGS);

  const [loadingSettings, setLoadingSettings] = useState(true);

  const [message, setMessage] = useState(null);

  const showMessage = (type, text) => {
    setMessage({ type, text });

    setTimeout(() => {
      setMessage(null);
    }, 4000);
  };

  const update = (key, value) => {
    const nextSettings = {
      ...settingsRef.current,
      [key]: value,
    };

    // Keep the value used by Save in sync immediately. React may defer the
    // state render, but a Save click must never submit the previous value.
    settingsRef.current = nextSettings;
    setSettings(nextSettings);
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data } = await axios.get(
          "/api/digital-risk/platform-settings/"
        );

        const normalized = normalizePlatformSettings({
          ...DEFAULT_SETTINGS,
          ...data,
        });

        settingsRef.current = normalized;
        setSettings(normalized);

        localStorage.setItem(
          "platformSettings",
          JSON.stringify(normalized)
        );
      } catch (err) {
        console.error("Error loading platform settings", err);
      } finally {
        setLoadingSettings(false);
      }
    };

    loadSettings();
  }, []);


  const saveSettings = async () => {
    try {
      const csrfToken = await ensureCsrfToken();
      const settingsToSave = settingsRef.current;

      await axios.put(
        "/api/digital-risk/platform-settings/",
        settingsToSave,
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
          },
        }
      );

      localStorage.setItem(
        "platformSettings",
        JSON.stringify(settingsToSave)
      );

      showMessage(
        "success",
        "Platform settings updated successfully."
      );
    } catch (err) {
      console.error(err);
      showMessage(
        "error",
        "Unable to save platform settings."
      );
    }
  };

  if (loadingSettings) {
    return (
      <div className="p-8">
        Loading platform settings...
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-indigo-50/40 to-white p-8">

      <div className="relative z-10 space-y-8">

        <div >
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
              Scenario Modeling
          </p>

          <h1 className="text-2xl font-semibold text-slate-900 mt-2">
            Platform Settings
          </h1>

          <p className="text-slate-500 mt-2 max-w-3xl mt-1">
            Configure governance thresholds, quantification parameters and executive reporting preferences.
          </p>

        </div>
    
        {message && (
          <div
            className={`rounded-xl border px-4 py-3 text-sm ${
              message.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            {message.text}
          </div>
        )}
      
      <div className=" flex justify-end gap-3">
        <button
            onClick={saveSettings}
            disabled={!canConfigure}
            title={!canConfigure ? "You have read-only access to Platform Settings." : undefined}
            className="px-5 py-3 rounded-xl bg-slate-950 text-white hover:bg-slate-800"
          >
            Save Settings
        </button>
        {!canConfigure && <p role="status" className="self-center text-sm text-slate-600">Read-only: configuration access is required to save settings.</p>}
      </div>
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <div className="xl:col-span-1 bg-white/85 backdrop-blur-sm border border-white/70 rounded-2xl p-4 shadow-sm h-fit">
            <div className="space-y-2">
              {sections.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSection(item.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm transition ${
                    section === item.id
                      ? "bg-slate-950 text-white"
                      : "hover:bg-slate-100 text-slate-700"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="xl:col-span-4 space-y-6">
            {section === "governance" && (
              <Card
                title="Governance Configuration"
                subtitle="Define executive thresholds and governance escalation logic."
              >
                <Field label="Risk Appetite">                 
                  <select
                    value={getMatchingRiskAppetite(settings)}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "Custom") {
                        return;
                      }

                      const preset =
                        RISK_APPETITE_PRESETS[value];

                      setSettings((prev) => ({
                        ...prev,
                        riskAppetite: value,
                        ...preset,
                      }));
                    }}
                    className="w-full border rounded-xl px-4 py-3"
                  >
                    <option>Low</option>
                    <option>Moderate</option>
                    <option>High</option>
                    <option>Aggressive</option>
                    <option>Custom</option>
                  </select>
                </Field>


                <Field label="Risk Appetite P95 Threshold">
                  <input
                    type="number"
                    value={settings.riskAppetiteP95}
                    onChange={(e) =>
                      update("riskAppetiteP95", Number(e.target.value))
                    }
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </Field>

                <Field label="Risk Appetite EAL Threshold">
                  <input
                    type="number"
                    value={settings.riskAppetiteEAL}
                    onChange={(e) =>
                      update("riskAppetiteEAL", Number(e.target.value))
                    }
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </Field>




                <Field label="Critical Exposure Threshold" help="Used to flag material risks for executive attention.">
                  <input
                    type="number"
                    value={settings.criticalExposureThreshold}
                    onChange={(e) =>
                      update("criticalExposureThreshold", Number(e.target.value))
                    }
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </Field>

                <Field label="Board Escalation Threshold">
                  <select
                    value={settings.boardEscalationThreshold}
                    onChange={(e) =>
                      update("boardEscalationThreshold", e.target.value)
                    }
                    className="w-full border rounded-xl px-4 py-3 bg-white"
                  >
                    <option>High</option>
                    <option>Critical</option>
                  </select>
                </Field>

                <Field label="Delayed Initiatives Threshold">
                  <input
                    type="number"
                    value={settings.delayedInitiativesThreshold}
                    onChange={(e) =>
                      update("delayedInitiativesThreshold", Number(e.target.value))
                    }
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </Field>
              </Card>
            )}

            {section === "quantification" && (
              <Card
                title="Quantification Settings"
                subtitle="Configure FAIR-based simulation and financial risk analytics preferences."
              >
                <Field label="Monte Carlo Iterations">
                  <select
                    value={settings.simulations}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        simulations: Number(e.target.value),
                      })
                    }
                  >
                    {FAIR_SIMULATION_OPTIONS.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>


                <Field label="Simulation Mode">
                  <select
                    value={settings.seedMode}
                    onChange={(e) => update("seedMode", e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 bg-white"
                  >
                    <option value="Fixed">Governance Mode</option>
                    <option value="Random">Exploratory Mode</option>
                  </select>
                </Field>

                {settings.seedMode === "Fixed" && (
                  <Field label="Seed Value">
                    <input
                      type="number"
                      value={settings.seedValue}
                      onChange={(e) => update("seedValue", Number(e.target.value))}
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </Field>
                )}



                <Field label="Confidence Level">
                  
                  <select
                    value={settings.confidenceLevel}
                    onChange={(e) => update("confidenceLevel", e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 bg-white"
                  >
                    <option>P90</option>
                    <option>P95</option>
                    <option>P99</option>
                  </select>

                </Field>

                <Field label="Currency">
                  <select
                    value={settings.currency}
                    onChange={(e) => update("currency", e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 bg-white"
                  >
                    <option>USD</option>
                    <option>EUR</option>
                    <option>GBP</option>
                  </select>
                </Field>

                <Field
                  label="Economic Horizon (years)"
                  help="Governed positive-integer horizon used by backend investment economics."
                >
                  <input
                    aria-label="Economic Horizon (years)"
                    type="number"
                    min="1"
                    step="1"
                    value={settings.economicHorizonYears}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (Number.isInteger(value) && value > 0) {
                        update("economicHorizonYears", value);
                      }
                    }}
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </Field>

               <Field label="Loss Model">
                  <select
                    value={settings.lossModel}
                    onChange={(e) => update("lossModel", e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 bg-white"
                  >
                    <option>Triangular</option>
                    <option>PERT</option>
                    <option>Lognormal</option>
                  </select>
                </Field>

                <Field
                  label="Investment Ceiling Ratio"
                  help="Maximum investment explored as a percentage of portfolio EAL. Example: 0.75 means 75% of current EAL."
                >
                  <input
                    type="number"
                    step="0.05"
                    min="0.1"
                    max="2"
                    value={settings.investmentCeilingRatio}
                    onChange={(e) =>
                      update("investmentCeilingRatio", Number(e.target.value))
                    }
                    className="w-full border rounded-xl px-3 py-2"
                  />
                </Field>

                <Field
                  label="Recommended Range Min Ratio"
                  help="Minimum recommended investment as a percentage of portfolio EAL. Example: 0.20 means 20% of current EAL."
                >
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="2"
                    value={settings.recommendedRangeMinRatio}
                    onChange={(e) =>
                      update("recommendedRangeMinRatio", Number(e.target.value))
                    }
                    className="w-full border rounded-xl px-3 py-2"
                  />
                </Field>

                <Field
                  label="Recommended Range Max Ratio"
                  help="Maximum recommended investment as a percentage of portfolio EAL. Example: 0.30 means 30% of current EAL."
                >
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="2"
                    value={settings.recommendedRangeMaxRatio}
                    onChange={(e) =>
                      update("recommendedRangeMaxRatio", Number(e.target.value))
                    }
                    className="w-full border rounded-xl px-3 py-2"
                  />
                </Field>

                  <div className="md:col-span-2 border-t pt-6 mt-2">
                    <h3 className="text-lg font-semibold text-slate-900">
                      Correlation Modeling
                    </h3>

                    <p className="text-sm text-slate-500 mt-1">
                      Configure how shared dependencies amplify portfolio tail-risk during Monte Carlo simulations.
                    </p>
                  </div>

                  <Field
                    label="Enable Correlation Modeling"
                    help="When enabled, shared dependencies can introduce portfolio-wide shock effects."
                  >
                    <input
                      type="checkbox"
                      checked={settings.enable_correlation_modeling}
                      onChange={(e) =>
                        update("enable_correlation_modeling", e.target.checked)
                      }
                    />
                  </Field>

                  <Field
                    label="Shared Dependency Failure Rate"
                    help="Annual probability that a shared dependency creates a systemic shock event."
                  >
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={settings.shared_dependency_failure_rate}
                      onChange={(e) =>
                        update("shared_dependency_failure_rate", Number(e.target.value))
                      }
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </Field>

                  <Field
                    label="Shock Multiplier Min"
                    help="Minimum loss multiplier applied when a shared dependency shock occurs."
                  >
                    <input
                      type="number"
                      step="0.05"
                      min="1"
                      value={settings.correlation_shock_min}
                      onChange={(e) =>
                        update("correlation_shock_min", Number(e.target.value))
                      }
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </Field>

                  <Field
                    label="Shock Multiplier Most Likely"
                    help="Most likely loss multiplier applied during correlated dependency shock events."
                  >
                    <input
                      type="number"
                      step="0.05"
                      min="1"
                      value={settings.correlation_shock_mode}
                      onChange={(e) =>
                        update("correlation_shock_mode", Number(e.target.value))
                      }
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </Field>

                  <Field
                    label="Shock Multiplier Max"
                    help="Maximum loss multiplier applied when correlation stress is severe."
                  >
                    <input
                      type="number"
                      step="0.05"
                      min="1"
                      value={settings.correlation_shock_max}
                      onChange={(e) =>
                        update("correlation_shock_max", Number(e.target.value))
                      }
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </Field>

                  <Field
                    label="Minimum Shared Scenarios"
                    help="Minimum number of scenarios sharing the same dependency before correlation stress is applied."
                  >
                    <input
                      type="number"
                      min="2"
                      value={settings.minimum_shared_dependency_count}
                      onChange={(e) =>
                        update("minimum_shared_dependency_count", Number(e.target.value))
                      }
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </Field>

              </Card>
            )}

            {section === "capital-efficiency" && (
              <Card
                title="Capital Efficiency Policy"
                subtitle="Configure the governed policy used to evaluate the Capital Efficient Investment Range."
              >
                <Field label="Policy ID">
                  <input
                    aria-label="Policy ID"
                    value={settings.capitalEfficiencyPolicyId ?? ""}
                    onChange={(e) => update("capitalEfficiencyPolicyId", e.target.value || null)}
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </Field>
                <Field label="Policy Version">
                  <input
                    aria-label="Policy Version"
                    value={settings.capitalEfficiencyPolicyVersion ?? ""}
                    onChange={(e) => update("capitalEfficiencyPolicyVersion", e.target.value || null)}
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </Field>
                <Field
                  label="Marginal Annual EAL Efficiency Hurdle"
                  help="Minimum additional annual EAL reduction required per unit of incremental portfolio funding. This is a ratio, not a percentage."
                >
                  <input
                    aria-label="Marginal Annual EAL Efficiency Hurdle"
                    type="number"
                    min="0"
                    step="any"
                    value={settings.marginalCapitalEfficiencyHurdle ?? ""}
                    onChange={(e) => update(
                      "marginalCapitalEfficiencyHurdle",
                      e.target.value === "" ? null : e.target.value
                    )}
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </Field>
                <Field
                  label="Minimum Retention Coverage Ratio"
                  help="Minimum Capital-at-Risk coverage per unit of committed reserve capital. This adequacy metric is not ROI and does not establish an optimal investment range."
                >
                  <input
                    aria-label="Minimum Retention Coverage Ratio"
                    type="number"
                    min="0"
                    step="any"
                    value={settings.marginalRetentionCoverageEfficiencyHurdle ?? ""}
                    onChange={(e) => update(
                      "marginalRetentionCoverageEfficiencyHurdle",
                      e.target.value === "" ? null : e.target.value
                    )}
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </Field>
                <Field
                  label="Mandatory Appetite Dimensions"
                  help="Risk-appetite conditions that must be satisfied before a portfolio position can become the lower bound of the Capital Efficient Investment Range."
                >
                  <div className="flex gap-6 rounded-xl border px-4 py-3">
                    {["EAL", "P95"].map((dimension) => (
                      <label key={dimension} className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={(settings.capitalEfficiencyMandatoryAppetiteDimensions || []).includes(dimension)}
                          onChange={(e) => {
                            const current = settings.capitalEfficiencyMandatoryAppetiteDimensions || [];
                            update(
                              "capitalEfficiencyMandatoryAppetiteDimensions",
                              e.target.checked
                                ? [...current, dimension]
                                : current.filter((item) => item !== dimension)
                            );
                          }}
                        />
                        {dimension}
                      </label>
                    ))}
                  </div>
                </Field>
              </Card>
            )}

            {section === "dashboards" && (
              <Card
                title="Dashboard Preferences"
                subtitle="Adjust executive dashboard behavior and default reporting views."
              >
                <Field label="Default Dashboard">
                  <select
                    value={settings.defaultDashboard}
                    onChange={(e) => update("defaultDashboard", e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 bg-white"
                  >
                    <option>Executive</option>
                    <option>Board</option>
                    <option>Operational</option>
                  </select>
                </Field>

                <Field label="Refresh Frequency">
                  <select
                    value={settings.refreshFrequency}
                    onChange={(e) => update("refreshFrequency", e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 bg-white"
                  >
                    <option>Realtime</option>
                    <option>Hourly</option>
                    <option>Daily</option>
                  </select>
                </Field>

                <Field label="Presentation Mode Default">
                  <input
                    type="checkbox"
                    checked={settings.presentationMode}
                    onChange={(e) => update("presentationMode", e.target.checked)}
                  />
                </Field>
              </Card>
            )}

            {section === "branding" && (
              <Card
                title="Branding"
                subtitle="Configure organization identity and executive reporting appearance."
              >
                <Field label="Organization Name">
                  <input
                    value={settings.organizationName}
                    onChange={(e) => update("organizationName", e.target.value)}
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </Field>

                <Field label="Report Theme">
                  <select
                    value={settings.reportTheme}
                    onChange={(e) => update("reportTheme", e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 bg-white"
                  >
                    <option>Executive Dark</option>
                    <option>Board Light</option>
                    <option>Minimal</option>
                  </select>
                </Field>
              </Card>
            )}

            {section === "platform" && (
              <Card
                title="Platform Intelligence"
                subtitle="Enable guidance layers and executive interpretation features."
              >
                <Field label="AI Guidance">
                  <input
                    type="checkbox"
                    checked={settings.aiGuidance}
                    onChange={(e) => update("aiGuidance", e.target.checked)}
                  />
                </Field>

                <Field label="Executive Interpretation Layer">
                  <input
                    type="checkbox"
                    checked={settings.executiveInterpretation}
                    onChange={(e) =>
                      update("executiveInterpretation", e.target.checked)
                    }
                  />
                </Field>

                <Field label="Governance Insights">
                  <input
                    type="checkbox"
                    checked={settings.governanceInsights}
                    onChange={(e) =>
                      update("governanceInsights", e.target.checked)
                    }
                  />
                </Field>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
